// Оформление: себе, сюрприз и подарок (адрес знаю / пусть получатель выберет сам).

import { useEffect, useRef, useState } from 'react';
import { api, type PromoT, type Size } from '../lib/api.ts';
import { useApp } from '../lib/store.tsx';
import { haptic, requestPhone } from '../lib/tg.ts';
import { onSite } from '../lib/web.ts';
import { track } from '../lib/events.ts';
import { dayWord, rub, slotText } from '../lib/format.ts';
import { BouquetArt, ErrorNote, Icon, TitleTop, toast } from '../ui/kit.tsx';
import { SURPRISE } from './result.tsx';

export interface Address {
  value: string;
  lat: number | null;
  lon: number | null;
  insideMkad: boolean | null;
}

// Та же логика, что на сервере (domain/delivery.ts) — только для подсказки цены до оформления.
export function zoneOf(a: Address | null): 1 | 2 | 3 | 4 | null {
  if (!a || a.lat === null || a.lon === null) return null;
  const rad = Math.PI / 180;
  const dLat = (a.lat - 55.752) * rad;
  const dLon = (a.lon - 37.6175) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(55.752 * rad) * Math.cos(a.lat * rad) * Math.sin(dLon / 2) ** 2;
  const km = 2 * 6371 * Math.asin(Math.sqrt(h));
  if (!(a.insideMkad ?? km <= 16)) return 4;
  return km <= 2.5 ? 1 : km <= 5.5 ? 2 : 3;
}

export function AddressField({ value, onChange, label = 'Адрес доставки', id = 'addr' }: { value: Address | null; onChange: (a: Address | null) => void; label?: string; id?: string }) {
  const { config } = useApp();
  const [text, setText] = useState(value?.value ?? '');
  const [list, setList] = useState<Address[]>([]);
  const [open, setOpen] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!config?.addressSuggest || text.length < 3 || text === value?.value) return setList([]);
    clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      api
        .suggest(text)
        .then((r) => setList(r.suggestions.filter((s) => s.lat !== null)))
        .catch(() => setList([]));
    }, 250);
    return () => clearTimeout(timer.current);
  }, [text, config?.addressSuggest]);

  return (
    <div className="field" style={{ position: 'relative' }}>
      <label htmlFor={id} style={{ fontSize: 14, fontWeight: 600 }}>
        {label}
      </label>
      <input
        id={id}
        className="input"
        autoComplete="street-address"
        placeholder="Улица и дом"
        value={text}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => {
          setText(e.target.value);
          // Без подсказок адрес принимаем как есть — цену доставки назовёт флорист.
          onChange(config?.addressSuggest ? null : e.target.value.trim().length > 5 ? { value: e.target.value.trim(), lat: null, lon: null, insideMkad: null } : null);
        }}
      />
      {open && list.length > 0 && (
        <div className="list" role="listbox" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 5, marginTop: 4, boxShadow: '0 8px 24px #3b332222' }}>
          {list.map((s) => (
            <button
              key={s.value}
              type="button"
              role="option"
              aria-selected={false}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setText(s.value);
                setList([]);
                setOpen(false);
                onChange(s);
              }}
              style={{ fontSize: 14 }}
            >
              {s.value}
            </button>
          ))}
        </div>
      )}
      {config?.addressSuggest && text.length > 5 && !value && !open && <span className="err-text">Выбери адрес из подсказок, так мы точно найдём дом</span>}
    </div>
  );
}

export function PhoneField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [manual, setManual] = useState(false);
  // Номер показываем целиком: это номер самого клиента, прятать его незачем.
  const digits = value.replace(/\D/g, '').replace(/^8(?=\d{10}$)/, '7');
  const pretty = /^7\d{10}$/.test(digits) ? `+7 ${digits.slice(1, 4)} ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9)}` : value;
  if (value && !manual && !onSite)
    return (
      <div className="card row" style={{ justifyContent: 'space-between' }}>
        <span className="row" style={{ gap: 10 }}>
          <Icon name="phone" size={18} color="#6B604A" width={1.8} />
          <span>{pretty}</span>
        </span>
        <button type="button" className="link" style={{ fontSize: 14, fontWeight: 600, minHeight: 36 }} onClick={() => setManual(true)}>
          Изменить
        </button>
      </div>
    );
  return (
    <div className="stack s6">
      {!manual && !onSite && (
        <button
          type="button"
          className="btn light"
          onClick={async () => {
            const p = await requestPhone();
            if (p) onChange(p.replace(/[^\d+]/g, ''));
            else setManual(true);
          }}
        >
          <svg aria-hidden="true" viewBox="0 0 24 24" width="20" height="20" fill="#2E8BD8">
            <path d="M21.5 4.3 2.9 11.5c-1.3.5-1.2 1.3-.2 1.6l4.8 1.5 1.8 5.6c.2.7.4.9.9.9.4 0 .6-.2.9-.5l2.3-2.2 4.8 3.5c.9.5 1.5.2 1.7-.8l3.1-14.7c.3-1.3-.5-1.9-1.5-1.1z" />
          </svg>
          Взять номер из Telegram
        </button>
      )}
      {(manual || !value || onSite) && (
        <input className="input" type="tel" inputMode="tel" autoComplete="tel" placeholder="+7 999 123-45-67" value={value} onChange={(e) => onChange(e.target.value)} aria-label="Телефон для чека и связи" />
      )}
    </div>
  );
}

const validPhone = (p: string) => p.replace(/\D/g, '').length >= 10;

export function Checkout() {
  const { go, flow, setFlow, me, reloadMe, config } = useApp();
  const gift = flow.mode === 'gift';
  const d = flow.design;
  const pre = flow.prefill ?? null;
  // Время выбирает тот, кто знает адрес: без слота подарок оформляется только как «получатель выберет сам».
  const [mode, setMode] = useState<'ask' | 'known'>(flow.slotByRecipient ? 'ask' : pre?.recipientMode ?? (gift ? (flow.slotFrom === null ? 'ask' : 'known') : 'known'));
  const [address, setAddress] = useState<Address | null>(pre?.address ?? null);
  const [extra, setExtra] = useState(pre?.extra ?? '');
  const [handover, setHandover] = useState<'hand' | 'door'>(pre?.handover ?? 'hand');
  const [rName, setRName] = useState(pre?.rName ?? (gift ? flow.personName ?? '' : ''));
  const [rPhone, setRPhone] = useState(pre?.rPhone ?? '');
  const [cardOpen, setCardOpen] = useState(pre?.cardOpen ?? Boolean(pre?.cardText));
  const [cardText, setCardText] = useState(pre?.cardText ?? '');
  const [phone, setPhone] = useState(pre?.phone || me?.user.phone || '');
  // Сайт: без Telegram пишем на почту (фото букета, статусы, чек).
  const [email, setEmail] = useState(pre?.email || me?.user.email || '');
  const [agree, setAgree] = useState(pre?.agree ?? Boolean(me?.user.consentPd));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Черновик формы живёт в flow: уйти в оферту, зоны или выбор времени и вернуться — ничего не теряется.
  useEffect(() => {
    setFlow({ prefill: { recipientMode: mode, address, extra, handover, rName, rPhone, cardText, cardOpen, phone, email, agree } });
  }, [mode, address, extra, handover, rName, rPhone, cardText, cardOpen, phone, email, agree]);

  useEffect(() => {
    if (me?.user.phone && !phone) setPhone(me.user.phone);
    if (me?.user.email && !email) setEmail(me.user.email);
    if (me?.user.consentPd) setAgree(true);
  }, [me]);

  const sizes: Size[] = ['S', 'M', 'L'];
  const priceOf = (s: Size) => (flow.surprise ? SURPRISE[s] : d?.sizes.find((x) => x.size === s && x.available)?.price ?? null);
  const item = priceOf(flow.size);
  const ask = gift && mode === 'ask';
  const zone = ask ? null : zoneOf(address);
  const delivery = ask ? config?.delivery.zone3 ?? null : zone === 1 ? config?.delivery.zone1 : zone === 2 ? config?.delivery.zone2 : zone === 3 ? config?.delivery.zone3 : null;
  // Промокоды: подходящие к этой корзине; применяется по галочке, один на заказ.
  const [promoList, setPromoList] = useState<PromoT[]>([]);
  const [promoId, setPromoId] = useState<number | null>(null);
  const cartKind = flow.surprise ? 'surprise' : gift ? 'gift' : 'self';
  const loadPromos = () => {
    if (!flow.date) return;
    api
      .promosForCart({ kind: cartKind, designId: flow.surprise ? undefined : d?.id, size: flow.size, date: flow.date })
      .then((r) => {
        setPromoList(r.promos);
        setPromoId((cur) => (r.promos.some((p) => p.id === cur && p.ok) ? cur : null));
      })
      .catch(() => setPromoList([]));
  };
  useEffect(loadPromos, [cartKind, d?.id, flow.size, flow.date]);
  const promo = promoList.find((p) => p.id === promoId && p.ok) ?? null;
  const discount = promo?.discount ?? 0;
  const total = (item ?? 0) - discount + (delivery ?? 0);
  useEffect(() => {
    track('checkout_started', { kind: flow.surprise ? 'surprise' : gift ? 'gift' : 'self', total, zone });
  }, []);
  const quote = !ask && address && (zone === 4 || zone === null);

  const missing =
    !item ? 'Этот размер сейчас не собрать, выбери другой' :
    !flow.date ? 'Выбери день доставки' :
    !ask && flow.slotFrom === null ? 'Выбери время доставки' :
    !ask && !address ? 'Укажи адрес доставки' :
    gift && !rName.trim() ? 'Как зовут получателя?' :
    gift && ask && !validPhone(rPhone) ? 'Нужен телефон получателя, курьер позвонит перед приездом' :
    !validPhone(phone) ? 'Нужен телефон для чека' :
    onSite && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? 'Нужна почта: пришлём туда фото букета и статусы' :
    !agree ? 'Нужно согласие с условиями' : null;

  const submit = async () => {
    if (missing) return (track('checkout_error', { message: missing }), setError(missing));
    setBusy(true);
    setError(null);
    try {
      if (!me?.user.consentPd) {
        await api.consent(me?.user.consentAds ?? false);
        reloadMe();
      }
      const o = await api.createOrder({
        kind: flow.surprise ? 'surprise' : gift ? 'gift' : 'self',
        designId: flow.surprise ? undefined : d!.id,
        size: flow.size,
        surpriseNotes: flow.surprise ? flow.surpriseNotes || undefined : flow.portraitNote || undefined,
        portrait: !flow.surprise && flow.portrait ? flow.portrait : undefined,
        personId: flow.personId ?? undefined,
        personName: gift && !flow.personId ? flow.personName ?? rName : undefined,
        relation: gift && !flow.personId ? flow.relation ?? 'other' : undefined,
        occasion: flow.occasion ?? undefined,
        date: flow.date,
        slotFrom: ask ? null : flow.slotFrom,
        recipientMode: gift ? mode : undefined,
        address: ask ? undefined : address!.value,
        addressExtra: ask ? undefined : extra || undefined,
        lat: ask ? undefined : address!.lat ?? undefined,
        lon: ask ? undefined : address!.lon ?? undefined,
        insideMkad: ask ? undefined : address!.insideMkad,
        handover,
        recipientName: gift ? rName.trim() : undefined,
        recipientPhone: gift && rPhone ? rPhone : undefined,
        cardText: cardOpen && cardText.trim() ? cardText.trim() : undefined,
        cardSigned: false,
        phone,
        email: onSite ? email.trim() : undefined,
        promoId: promo?.id,
      });
      haptic.ok();
      if (promo) track('promo_applied', { kind: promo.kind, discount: promo.discount ?? 0 }, { orderId: o.publicId });
      setFlow({ prefill: null });
      if (o.status === 'draft') go({ name: 'order', orderId: o.publicId });
      else go({ name: 'pay', orderId: o.publicId });
    } catch (e: any) {
      haptic.err();
      track('checkout_error', { message: String(e.message).slice(0, 60) });
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="screen" style={{ gap: 16 }}>
      <TitleTop title={gift ? 'Кому и как вручить' : 'Почти готово'} eyebrow={gift ? (flow.relation === 'mom' ? 'Подарок для мамы' : 'Подарок') : undefined} />

      <div className="card row" style={{ padding: 10 }}>
        <div style={{ width: 56, height: 56, borderRadius: 14, overflow: 'hidden', flex: 'none' }}>
          {d ? <BouquetArt design={d} height={56} /> : <div className="art" style={{ height: 56, background: 'var(--pink)' }} />}
        </div>
        <span className="stack s6" style={{ gap: 2 }}>
          <span style={{ fontFamily: 'var(--serif)', fontSize: 19 }}>{flow.surprise ? 'Сюрприз от флориста' : d?.name}</span>
          <span className="small">{d?.description ?? 'Из самых свежих цветов дня'}</span>
        </span>
      </div>

      <div className="stack s6">
        <span className="label">Размер</span>
        <div className="grid3">
          {sizes.map((s) => {
            const p = priceOf(s);
            return (
              <button key={s} type="button" className="tile" disabled={!p} aria-pressed={flow.size === s} onClick={() => setFlow({ size: s })} style={{ flexDirection: 'column', gap: 0, justifyContent: 'center', minHeight: 56, padding: 6, opacity: p ? 1 : 0.4 }}>
                <span style={{ fontWeight: 600 }}>{s}</span>
                <span className="tiny">{p ? rub(p) : 'нет в наличии'}</span>
              </button>
            );
          })}
        </div>
      </div>

      <button type="button" className="card row" style={{ textAlign: 'left', width: '100%' }} onClick={() => go({ name: 'when', returnTo: 'checkout' })}>
        <Icon name="calendar" size={20} color="#6B604A" width={1.8} />
        <span className="stack s6" style={{ gap: 0, flex: 1 }}>
          <span style={{ fontWeight: 600 }}>
            {!flow.date ? (ask ? 'Выбери день' : 'Выбери день и время') : ask ? dayWord(flow.date) : flow.slotFrom === null ? `${dayWord(flow.date)}, выбери время` : `${dayWord(flow.date)}, ${slotText(flow.slotFrom, flow.slotFrom + (config?.shop.windowHours ?? 2))}`}
          </span>
          <span className="tiny">{ask ? 'Время получатель выберет сам по ссылке' : 'Курьер приедет в любой момент внутри окна'}</span>
        </span>
        <span style={{ fontSize: 14, fontWeight: 600 }}>{flow.date && (ask || flow.slotFrom !== null) ? 'Изменить' : 'Выбрать'}</span>
      </button>

      {gift && (
        <div className="stack">
          <span className="label">Получатель</span>
          <input className="input" placeholder="Имя" value={rName} maxLength={60} onChange={(e) => setRName(e.target.value)} aria-label="Имя получателя" />
          <input className="input" type="tel" inputMode="tel" placeholder="Телефон получателя" value={rPhone} onChange={(e) => setRPhone(e.target.value)} aria-label="Телефон получателя" />
          <span className="label" style={{ marginTop: 6 }}>
            Адрес и время
          </span>
          {(
            [
              ['ask', 'Пусть получатель выберет сам', 'Адрес и время по ссылке, сюрприз не испортим'],
              ['known', 'Я знаю адрес', 'Укажу адрес и время'],
            ] as const
          ).map(([id, t, sub]) => (
            <button key={id} type="button" className="tile" aria-pressed={mode === id} onClick={() => setMode(id)} style={{ minHeight: 56, padding: '10px 14px', background: 'var(--white)', borderColor: mode === id ? 'var(--ink)' : 'var(--line)' }}>
              <span style={{ width: 20, height: 20, borderRadius: 10, flex: 'none', ...(mode === id ? { background: 'var(--ink)', border: '5px solid var(--butter)' } : { border: '1.5px solid var(--line-2)' }) }} />
              <span className="stack s6" style={{ gap: 0 }}>
                <span style={{ fontWeight: 500 }}>{t}</span>
                <span className="tiny">{sub}</span>
              </span>
            </button>
          ))}
          {ask && (
            <div className="note">
              <Icon name="info" size={18} />
              <span>
                Доставка по Москве внутри МКАД стоит {rub(config?.delivery.zone3 ?? null)}. Если адрес окажется за МКАД, флорист посчитает доплату.
              </span>
            </div>
          )}
        </div>
      )}

      {!ask && (
        <div className="stack">
          <AddressField value={address} onChange={setAddress} />
          <input className="input" placeholder="Квартира, подъезд, домофон, этаж" value={extra} maxLength={200} onChange={(e) => setExtra(e.target.value)} aria-label="Подсказка курьеру" />
          {address && (
            <span className="tiny">
              {zone === 4 ? 'Это за МКАД. Флорист посчитает доставку в течение часа и пришлёт цену сюда.' : zone ? `Зона ${zone}, доставка ${rub(delivery ?? null)}.` : 'Цену доставки назовёт флорист после оформления.'}{' '}
              <button type="button" className="link" onClick={() => go({ name: 'zones' })}>
                Зоны и цены
              </button>
            </span>
          )}
          {!address && (
            <span className="tiny">
              Цена доставки зависит от зоны, посчитаем по адресу.{' '}
              <button type="button" className="link" onClick={() => go({ name: 'zones' })}>
                Зоны и цены
              </button>
            </span>
          )}
          <span className="label" style={{ marginTop: 6 }}>
            Как вручить
          </span>
          {(
            [
              ['hand', 'Лично в руки', 'Курьер позвонит и подождёт до 15 минут'],
              ['door', 'Оставить у двери', 'Без звонков. Пришлём фото букета у двери'],
            ] as const
          ).map(([id, t, sub]) => (
            <button key={id} type="button" className="tile" aria-pressed={handover === id} onClick={() => setHandover(id)} style={{ minHeight: 56, padding: '10px 14px', background: 'var(--white)', borderColor: handover === id ? 'var(--ink)' : 'var(--line)' }}>
              <span style={{ width: 20, height: 20, borderRadius: 10, flex: 'none', ...(handover === id ? { background: 'var(--ink)', border: '5px solid var(--butter)' } : { border: '1.5px solid var(--line-2)' }) }} />
              <span className="stack s6" style={{ gap: 0 }}>
                <span style={{ fontWeight: 500 }}>{t}</span>
                <span className="tiny">{sub}</span>
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="card" style={{ padding: 0 }}>
        <button type="button" aria-expanded={cardOpen} onClick={() => setCardOpen(!cardOpen)} className="row" style={{ width: '100%', background: 'none', border: 'none', padding: '14px 16px', textAlign: 'left' }}>
          <Icon name="mail" size={20} color="#6B604A" width={1.8} />
          <span className="stack s6" style={{ gap: 0, flex: 1 }}>
            <span style={{ fontWeight: 600 }}>Открытка</span>
            <span className="tiny">{cardOpen ? 'Напиши текст ниже' : 'Без открытки'}</span>
          </span>
          <span style={{ transform: cardOpen ? 'rotate(180deg)' : undefined, display: 'flex' }}>
            <Icon name="down" size={18} />
          </span>
        </button>
        {cardOpen && (
          <div className="stack" style={{ padding: '0 16px 16px' }}>
            {gift && (
              <div className="wrap">
                {['С днём рождения!', 'Люблю тебя', 'Спасибо тебе', 'Прости меня'].map((t) => (
                  <button key={t} type="button" className="chip sm" onClick={() => setCardText(t)}>
                    {t}
                  </button>
                ))}
              </div>
            )}
            <textarea className="input" maxLength={300} placeholder="Текст открытки, до 300 знаков" value={cardText} onChange={(e) => setCardText(e.target.value)} aria-label="Текст открытки" />
          </div>
        )}
      </div>

      <div className="note">
        <Icon name="camera" size={18} />
        <span>
          {onSite ? 'Перед отправкой покажем фото готового букета на странице заказа и пришлём на почту.' : 'Перед отправкой пришлём тебе фото готового букета.'}
          {gift ? ' Получатель его не увидит.' : ''}
        </span>
      </div>

      <div className="stack s6">
        <span className="label">Чек и связь по заказу</span>
        <PhoneField value={phone} onChange={setPhone} />
        {onSite && (
          <>
            <input className="input" type="email" inputMode="email" autoComplete="email" placeholder="Почта" value={email} maxLength={120} onChange={(e) => setEmail(e.target.value)} aria-label="Почта" />
            <span className="tiny">Без регистрации. На почту пришлём фото букета перед отправкой, статусы и чек.</span>
          </>
        )}
      </div>

      <PromoBlock list={promoList} picked={promoId} onPick={setPromoId} onRedeemed={loadPromos} />

      <div className="card stack s6" style={{ fontSize: 15 }}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <span style={{ color: 'var(--ink-2)' }}>{flow.surprise ? 'Сюрприз' : `Букет «${d?.name}»`}, {flow.size}</span>
          <span className="price">{rub(item)}</span>
        </div>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <span style={{ color: 'var(--ink-2)' }}>{ask ? 'Доставка внутри МКАД' : 'Доставка'}</span>
          <span className="price">{quote ? 'посчитает флорист' : delivery !== null && delivery !== undefined ? rub(delivery) : '—'}</span>
        </div>
        {promo && (
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span style={{ color: 'var(--ink-2)' }}>Промокод</span>
            <span className="price" style={{ color: 'var(--ok-ink)' }}>−{rub(discount)}</span>
          </div>
        )}
        <div className="row" style={{ justifyContent: 'space-between', fontWeight: 700, paddingTop: 8, borderTop: '1px solid #F1EDE0' }}>
          <span>Итого</span>
          <span className="price">{quote ? `${rub((item ?? 0) - discount)} + доставка` : rub(total)}</span>
        </div>
      </div>

      <label className="check">
        <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
        <span>
          Принимаю условия{' '}
          <button type="button" className="link" onClick={() => go({ name: 'docs' })}>
            оферты
          </button>{' '}
          и даю согласие на обработку данных
        </span>
      </label>

      <ErrorNote error={error} />
      <div className="bottom">
        <button type="button" className="btn" disabled={busy} onClick={submit}>
          {busy ? 'Оформляем…' : quote ? 'Узнать цену доставки' : config?.payments === 'manual' ? `Оформить заказ · ${rub(total)}` : `Оплатить ${rub(total)}`}
        </button>
        <p className="tiny center" style={{ margin: 0 }}>
          Цветы живые, поэтому букет может немного отличаться от фото.
        </p>
      </div>
    </main>
  );
}

/**
 * Промокоды в оформлении. Выбрать можно один (или «Без промокода»).
 * Подходящие — по выгоде, первые три сразу, остальные по «Ещё N подходящих».
 * Неподходящие свёрнуты в одну строку, по нажатию — список с причинами.
 * Промокодов нет совсем — остаётся только «Есть промокод?».
 */
function PromoBlock({ list, picked, onPick, onRedeemed }: { list: PromoT[]; picked: number | null; onPick: (id: number | null) => void; onRedeemed: () => void }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [allOk, setAllOk] = useState(false);
  const [showBad, setShowBad] = useState(false);

  const exp = (p: PromoT) => (p.expiresAt ? Date.parse(p.expiresAt) : Infinity);
  const ok = list.filter((p) => p.ok).sort((a, b) => (b.discount ?? 0) - (a.discount ?? 0) || exp(a) - exp(b));
  const bad = list.filter((p) => !p.ok);
  const bestId = ok.length > 1 ? ok[0].id : null;
  // Выбранный промокод всегда виден, даже если он не в первой тройке.
  const shown = allOk ? ok : ok.slice(0, 3).concat(ok.slice(3).filter((p) => p.id === picked));
  const word = (n: number, one: string, few: string, many: string) => (n % 10 === 1 && n % 100 !== 11 ? one : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? few : many);

  const radio = (key: string | number, checked: boolean, onChange: () => void, title: string, sub?: string, badge?: string) => (
    <label className="check" key={key}>
      <input type="radio" name="promo" checked={checked} onChange={onChange} />
      <span>
        {title}
        {badge && (
          <span className="badge ok" style={{ marginLeft: 8, fontSize: 11 }}>
            {badge}
          </span>
        )}
        {sub && (
          <>
            <br />
            <span className="tiny">{sub}</span>
          </>
        )}
      </span>
    </label>
  );

  return (
    <div className="stack s6">
      {ok.length > 0 && (
        <>
          <span className="label">Промокоды</span>
          {shown.map((p) =>
            radio(p.id, picked === p.id, () => onPick(p.id), p.title, [p.discount ? `−${rub(p.discount)}` : '', p.until ? `действует ${p.until}` : ''].filter(Boolean).join(' · '), p.id === bestId ? 'выгоднее всего' : undefined),
          )}
          {!allOk && ok.length > 3 && (
            <button type="button" className="link" style={{ alignSelf: 'flex-start', fontSize: 14, minHeight: 36 }} onClick={() => setAllOk(true)}>
              Ещё {ok.length - 3} {word(ok.length - 3, 'подходящий', 'подходящих', 'подходящих')}
            </button>
          )}
          {radio('none', picked === null, () => onPick(null), 'Без промокода')}
        </>
      )}
      {bad.length > 0 && (
        <button type="button" className="quiet" style={{ alignSelf: 'flex-start', fontSize: 13, minHeight: 36, padding: 0, textAlign: 'left' }} onClick={() => setShowBad(!showBad)}>
          {ok.length ? 'Ещё' : 'У тебя'} {bad.length} {word(bad.length, 'промокод', 'промокода', 'промокодов')} {word(bad.length, 'не подходит', 'не подходят', 'не подходят')} к этому заказу {showBad ? '▴' : '▾'}
        </button>
      )}
      {showBad &&
        bad.map((p) => (
          <span key={p.id} className="tiny" style={{ paddingLeft: 4 }}>
            {p.title}: {p.reason?.replace(/^./, (c) => c.toLowerCase())}
          </span>
        ))}
      {!open ? (
        <button type="button" className="link" style={{ alignSelf: 'flex-start', fontSize: 14, fontWeight: 600, minHeight: 40 }} onClick={() => setOpen(true)}>
          Есть промокод?
        </button>
      ) : (
        <div className="row" style={{ gap: 8 }}>
          <input className="input" style={{ flex: 1 }} placeholder="Промокод" autoCapitalize="characters" value={code} maxLength={40} onChange={(e) => (setCode(e.target.value), setErr(null))} />
          <button
            type="button"
            className="btn sm"
            disabled={busy || code.trim().length < 2}
            onClick={async () => {
              setBusy(true);
              try {
                await api.redeemPromo(code.trim());
                setCode('');
                setOpen(false);
                toast('Промокод добавлен');
                onRedeemed();
              } catch (e: any) {
                setErr(e.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Применить
          </button>
        </div>
      )}
      {err && <span className="err-text">{err}</span>}
    </div>
  );
}

export function Zones() {
  const { config, back } = useApp();
  const p = config?.delivery;
  const rows: Array<[string, string, string]> = [
    ['1', 'Центр, внутри Садового', p?.zone1 != null ? rub(p.zone1) : '—'],
    ['2', 'От Садового до ТТК', p?.zone2 != null ? rub(p.zone2) : '—'],
    ['3', 'От ТТК до МКАД', p?.zone3 != null ? rub(p.zone3) : '—'],
    ['4', 'За МКАД', 'отдельно'],
  ];
  return (
    <main className="screen" style={{ gap: 14 }}>
      <TitleTop title="Зоны и цены" />
      <div className="list">
        {rows.map(([n, t, price]) => (
          <div key={n}>
            <span style={{ width: 26, height: 26, borderRadius: 13, background: n === '4' ? 'var(--sand)' : n === '1' ? 'var(--pink-line)' : n === '2' ? 'var(--pink)' : 'var(--butter-soft)', border: n === '4' ? '1px dashed var(--line-2)' : undefined, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, flex: 'none' }}>{n}</span>
            <span className="stack s6" style={{ gap: 0, flex: 1 }}>
              <span>{t}</span>
              {n === '4' && <span className="tiny">посчитаем в течение часа</span>}
            </span>
            <span className="price">{price}</span>
          </div>
        ))}
      </div>
      <ul className="small" style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6, color: 'var(--ink-2)' }}>
        <li>Цена не зависит от размера букета.</li>
        <li>Окно доставки 2 часа, курьер приезжает в любой момент внутри него.</li>
        <li>Букет едет в переноске с водой, поэтому не помнётся и не завянет в дороге.</li>
      </ul>
      <button type="button" className="btn outline" style={{ marginTop: 'auto' }} onClick={back}>
        Понятно
      </button>
    </main>
  );
}
